/**
 * Runtime configuration. The static build reads `/config.json` (served by nginx,
 * templated from env at container start), so one image runs in every environment.
 * `mock: null` in config.json means "use the build default" (VITE_MOCK, on by default
 * in dev until the API is ready).
 *
 * Observability (all optional, off when empty):
 * - `otlpEndpoint`  OpenTelemetry collector base path/URL; "/otlp" = same origin (Caddy/nginx → collector).
 *                   Browser spans (document load, fetch) export to `<otlpEndpoint>/v1/traces` and every /v1 call
 *                   carries a W3C `traceparent`, so one trace spans browser → API → DB → worker.
 * - `otelSampleRatio` share of page loads traced (0..1, default 1)
 * - `otelUserInteraction` also trace clicks/submits (default false)
 * - `sentryDsn`     GlitchTip (Sentry-compatible) DSN for frontend errors
 * - `release`       build/git sha, tagged on spans and error reports
 *
 * Support contacts (ALP-84 "Contact us to change plan"; optional, a LinkedIn message is the fallback):
 * - `supportEmail`    support e-mail address (MALFAK_SUPPORT_EMAIL / VITE_SUPPORT_EMAIL)
 * - `supportWhatsapp` WhatsApp number in international form, digits only (MALFAK_SUPPORT_WHATSAPP / VITE_SUPPORT_WHATSAPP)
 */
export type RuntimeConfig = {
  apiBaseUrl: string;
  mock: boolean;
  environment: string;
  release: string;
  otlpEndpoint: string;
  otelSampleRatio: number;
  otelUserInteraction: boolean;
  sentryDsn: string;
  supportEmail: string;
  supportWhatsapp: string;
};

type RawConfig = Partial<{
  apiBaseUrl: string;
  mock: boolean | null;
  environment: string;
  release: string;
  otlpEndpoint: string | null;
  otelSampleRatio: number | string | null;
  otelUserInteraction: boolean | null;
  sentryDsn: string | null;
  supportEmail: string | null;
  supportWhatsapp: string | null;
}>;

const buildDefaultMock = (import.meta.env.VITE_MOCK ?? "1") !== "0";

/** Build-time defaults (local dev server: `./malfak up web` sets these when the observability profile is on). */
const buildDefaults = (): RuntimeConfig => ({
  apiBaseUrl: "",
  mock: buildDefaultMock,
  environment: "local",
  release: import.meta.env.VITE_RELEASE ?? "dev",
  otlpEndpoint: import.meta.env.VITE_OTLP_ENDPOINT ?? "",
  otelSampleRatio: 1,
  otelUserInteraction: false,
  sentryDsn: import.meta.env.VITE_SENTRY_DSN ?? "",
  supportEmail: import.meta.env.VITE_SUPPORT_EMAIL ?? "",
  supportWhatsapp: import.meta.env.VITE_SUPPORT_WHATSAPP ?? "",
});

let current: RuntimeConfig = buildDefaults();

const ratio = (v: unknown, fallback: number): number => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : fallback;
};

/** Merges a /config.json document over the build defaults (exported for tests). */
export function parseRuntimeConfig(raw: RawConfig, base: RuntimeConfig = buildDefaults()): RuntimeConfig {
  return {
    apiBaseUrl: (raw.apiBaseUrl ?? "").replace(/\/$/, ""),
    mock: typeof raw.mock === "boolean" ? raw.mock : base.mock,
    environment: raw.environment ?? "local",
    release: raw.release || base.release,
    otlpEndpoint: (typeof raw.otlpEndpoint === "string" ? raw.otlpEndpoint : base.otlpEndpoint).trim().replace(/\/+$/, ""),
    otelSampleRatio: ratio(raw.otelSampleRatio, base.otelSampleRatio),
    otelUserInteraction: typeof raw.otelUserInteraction === "boolean" ? raw.otelUserInteraction : base.otelUserInteraction,
    sentryDsn: (typeof raw.sentryDsn === "string" ? raw.sentryDsn : base.sentryDsn).trim(),
    supportEmail: (typeof raw.supportEmail === "string" && raw.supportEmail.trim() ? raw.supportEmail : base.supportEmail).trim(),
    supportWhatsapp: (typeof raw.supportWhatsapp === "string" && raw.supportWhatsapp.trim() ? raw.supportWhatsapp : base.supportWhatsapp).replace(/[^0-9]/g, ""),
  };
}

export async function loadConfig(): Promise<RuntimeConfig> {
  try {
    const res = await fetch("/config.json", { cache: "no-store" });
    if (res.ok) current = parseRuntimeConfig((await res.json()) as RawConfig);
  } catch {
    /* no config.json (tests, odd hosting): keep the build defaults */
  }
  // ?mock=0 / ?mock=1 overrides for quick checks against a running API
  const q = new URLSearchParams(location.search).get("mock");
  if (q === "0" || q === "1") current = { ...current, mock: q === "1" };
  return current;
}

export const config = () => current;
export function setConfig(next: Partial<RuntimeConfig>) {
  current = { ...current, ...next };
}
